import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const VolunteerOpportunities = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
  { id: 'all', label: 'All Opportunities', icon: 'Grid3x3' },
  { id: 'conservation', label: 'Conservation', icon: 'Trees' },
  { id: 'education', label: 'Education', icon: 'GraduationCap' },
  { id: 'research', label: 'Research', icon: 'FlaskConical' },
  { id: 'tourism', label: 'Eco-Tourism', icon: 'Compass' }];


  const opportunities = [
  {
    id: 1,
    title: "Wildlife Monitoring Assistant",
    category: "conservation",
    location: "Core Conservation Zone",
    duration: "3-6 months",
    commitment: "20 hours/week",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1374be66c-1764851173928.png",
    imageAlt: "Conservation volunteer in khaki field uniform using binoculars to monitor wildlife in dense tropical rainforest canopy with notebook and camera equipment",
    description: "Support our research team in tracking endemic species populations and documenting behavioral patterns in their natural habitat.",
    skills: ["Wildlife observation", "Data recording", "Photography"],
    impact: "Direct contribution to species conservation database",
    volunteers: 12,
    available: 3
  },
  {
    id: 2,
    title: "Community Education Facilitator",
    category: "education",
    location: "Local Schools & Communities",
    duration: "6-12 months",
    commitment: "15 hours/week",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_168cdad4d-1764830567737.png",
    imageAlt: "Filipino educator teaching group of enthusiastic children about environmental conservation in outdoor classroom setting with colorful educational materials and nature displays",
    description: "Develop and deliver environmental education programs to local schools, teaching children about biodiversity conservation and sustainable practices.",
    skills: ["Public speaking", "Curriculum development", "Filipino/English fluency"],
    impact: "Reaching 500+ students annually",
    volunteers: 8,
    available: 2
  },
  {
    id: 3,
    title: "Traditional Knowledge Documenter",
    category: "research",
    location: "Indigenous Communities",
    duration: "Ongoing",
    commitment: "10 hours/week",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1227d8a53-1764661706740.png",
    imageAlt: "Elderly indigenous Filipino elder sharing traditional ecological knowledge with young researcher in traditional bamboo house surrounded by medicinal plants and cultural artifacts",
    description: "Work with indigenous elders to document traditional conservation practices, medicinal plant knowledge, and sustainable resource management techniques.",
    skills: ["Interview techniques", "Cultural sensitivity", "Documentation"],
    impact: "Preserving centuries of indigenous wisdom",
    volunteers: 5,
    available: 4
  },
  {
    id: 4,
    title: "Eco-Tourism Guide Trainee",
    category: "tourism",
    location: "Visitor Center & Trails",
    duration: "3 months training + ongoing",
    commitment: "25 hours/week",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ce32cea9-1765151405283.png",
    imageAlt: "Professional eco-tourism guide in official uniform leading diverse group of international tourists through limestone cave formation with headlamps and safety equipment",
    description: "Train to become a certified eco-tourism guide, leading visitors through the underground river while sharing conservation messages and local culture.",
    skills: ["Customer service", "Storytelling", "Physical fitness"],
    impact: "Sustainable livelihood creation",
    volunteers: 15,
    available: 6
  },
  {
    id: 5,
    title: "Reforestation Team Member",
    category: "conservation",
    location: "Buffer Zone Areas",
    duration: "Seasonal (June-November)",
    commitment: "Full-time",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ca4bc324-1764661982101.png",
    imageAlt: "Team of volunteers in matching green shirts planting native tree saplings in cleared forest area with mountains in background and organized rows of young trees",
    description: "Join intensive reforestation efforts planting native species in degraded areas, maintaining nurseries, and monitoring seedling survival rates.",
    skills: ["Physical labor", "Plant identification", "Teamwork"],
    impact: "10,000+ trees planted annually",
    volunteers: 25,
    available: 10
  },
  {
    id: 6,
    title: "Marine Debris Cleanup Coordinator",
    category: "conservation",
    location: "Coastal Areas",
    duration: "6 months",
    commitment: "20 hours/week",
    image: "https://images.unsplash.com/photo-1619341374997-13a03b09cf07",
    imageAlt: 'Stunning white sand beach with crystal-clear turquoise waters and dramatic limestone karst formations showcasing pristine coastal environment protected by marine debris cleanup initiatives',
    description: "Organize and lead monthly coastal cleanup events, engage local communities, and document marine debris data for research purposes.",
    skills: ["Event coordination", "Community mobilization", "Data collection"],
    impact: "2+ tons of debris removed monthly",
    volunteers: 18,
    available: 5
  }];


  const filteredOpportunities = selectedCategory === 'all' ?
  opportunities :
  opportunities?.filter((opp) => opp?.category === selectedCategory);

  return (
    <section className="py-16 bg-[var(--color-background)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--color-foreground)] mb-4">
            Volunteer Opportunities
          </h2>
          <p className="text-lg text-[var(--color-muted-foreground)] max-w-3xl mx-auto">
            Make a tangible difference in conservation efforts. Choose from diverse opportunities matching your skills, interests, and availability.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories?.map((category) =>
          <button
            key={category?.id}
            onClick={() => setSelectedCategory(category?.id)}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all border border-[var(--color-border)] ${
            selectedCategory === category?.id ?
            'bg-primary text-white shadow-conservation' :
            'bg-[var(--color-card)] text-[var(--color-foreground)] hover:bg-[var(--color-muted)]/50'}`
            }>

              <Icon name={category?.icon} size={20} />
              <span>{category?.label}</span>
            </button>
          )}
        </div>

        {/* Opportunities Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredOpportunities?.map((opportunity) =>
          <div
            key={opportunity?.id}
            className="bg-[var(--color-card)] rounded-lg overflow-hidden shadow-conservation hover:shadow-conservation-lg transition-all animation-organic group border border-[var(--color-border)]">

              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <Image
                src={opportunity?.image}
                alt={opportunity?.imageAlt}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />

                <div className="absolute top-4 right-4 bg-accent text-[var(--color-foreground)] px-3 py-1 rounded-full text-sm font-medium">
                  {opportunity?.available} spots left
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-xl font-bold text-[var(--color-foreground)] mb-2 group-hover:text-accent transition-colors">
                  {opportunity?.title}
                </h3>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
                    <Icon name="MapPin" size={16} />
                    <span>{opportunity?.location}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
                    <Icon name="Clock" size={16} />
                    <span>{opportunity?.duration} • {opportunity?.commitment}</span>
                  </div>
                </div>

                <p className="text-[var(--color-muted-foreground)] text-sm mb-4 line-clamp-3">
                  {opportunity?.description}
                </p>

                {/* Skills */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {opportunity?.skills?.map((skill, index) =>
                <span
                  key={index}
                  className="px-3 py-1 bg-[var(--color-muted)]/50 text-[var(--color-muted-foreground)] rounded-full text-xs">

                      {skill}
                    </span>
                )}
                </div>

                {/* Impact */}
                <div className="flex items-start gap-2 mb-4 p-3 bg-success/10 rounded-lg">
                  <Icon name="Target" size={16} className="text-success mt-0.5 flex-shrink-0" />
                  <span className="text-sm text-success">{opportunity?.impact}</span>
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div className="flex items-center gap-2 text-sm text-[var(--color-muted-foreground)]">
                    <Icon name="Users" size={16} />
                    <span>{opportunity?.volunteers} volunteers</span>
                  </div>
                  <Button variant="outline" size="sm">
                    Apply Now
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>);

};

export default VolunteerOpportunities;